/**
 * Top-up requests (vitrine client). VITRINE_TOPUP_REQUEST_DESIGN §7.1. The client fills how much + in
 * what currency they want to send and the source they will send FROM; Tacita moves no money. The client
 * view never carries screening/reject internals.
 */
import { http } from './http';

export type SourceKind = 'CRYPTO' | 'BANK' | 'CASH';

export interface TopUpView {
  id: string;
  status: string;
  /**
   * The DECLARED amount — null on the crypto rail, where the client sends what they want (REQ-S1) and the
   * column is nullable since V139. This type said plain `string` for months, so `vue-tsc` had nothing to
   * complain about when the list row rendered `fmtDisplay(null)` and produced a row reading just "USDT".
   */
  amount: string | null;
  amountCurrency: string;
  creditCurrency: string | null;
  sourceKind: SourceKind;
  ticketId: string | null;
  markedSent: boolean;
  createdAt: string;
  // Until when the quote holds (§3.5) — drives the "pay by" timer. Reconciliation amounts/anchor are
  // operator-only and are NOT exposed here.
  fundingDeadline: string | null;
  /**
   * The provider's pay-in requisites, once the payment team has returned them. The client sends the money
   * themselves — Tacita never holds it — so this is the one thing that turns a submitted request into
   * something actionable. Null until the requisites arrive.
   */
  payInInstructions: string | null;
  /** Deposit-rail block (EPHEMERAL_DEPOSIT_WALLETS_DESIGN §6.1) — null for non-rail requests. */
  deposit: TopUpDepositView | null;
  /**
   * Where the client's money went: "sent 100 → 97 to the card · fee 3", every number as the client SEES it.
   * Null until money arrived, off the rail, and while the tariff is unpriced.
   */
  breakdown: TopUpBreakdown | null;
  /** A manager opened this request on the client's behalf — "Opened by your manager". Absent on an older core. */
  openedByManager?: boolean;
  /**
   * How much of the client's money actually arrived. Major units — the same scale as `amount`.
   *
   * Denominated in what was received: the deposit asset on a rail request, `creditCurrency` otherwise.
   * Zero until the first credit, never null.
   */
  creditedAmount: string;
  /** Part of the money arrived and the request is still short. The gap and the recon verdict stay operator-only. */
  awaitingRemainder: boolean;
  /**
   * The server's answer to "will cancel actually work". The screen used to decide that from the status and
   * offered an action the server refused with a 409 (§3 of the 16.09 run).
   */
  cancellable: boolean;
  /**
   * PayFlow spec 07 В1: what the client asked to have credited to the card, in the card's currency — set instead of
   * `amount` on a bank/cash request funded through Transfer Hub (`amountCurrency` is then the currency they send in).
   * Absent on an older core.
   */
  requestedCreditAmount?: string | null;
  requestedCreditCurrency?: string | null;
  /** «Currency first»: what the client wants on the account, in `amountCurrency` (3000 EUR). Absent on an older core. */
  requestedAccountAmount?: string | null;
  /** «Currency first»: what the client pays for it, everything on top, in `amountCurrency` (the estimate, 3152.43 EUR). */
  requestedPayAmount?: string | null;
  /** PayFlow spec 07 T17: the payment service's reference for this request; absent until it answers. */
  paymentId?: string | null;
  /** T30: what the payment service needs from the client while the request waits for information. */
  needsInfoReason?: string | null;
  /** T29: "Request a refund" replaces "Cancel" — the payment service already holds the client's money. */
  refundRequestable?: boolean;
  /** T29: when the client asked for a refund; null — not asked. */
  refundRequestedAt?: string | null;
  /** T33: the "Reply" field is offered under the information request. */
  canReply?: boolean;
  /**
   * QA 14.10: what the client transfers for the credit they asked for — the form's lines on the request screen.
   * Null off the bank/cash credit path and once the request is closed; absent on an older core.
   */
  payment?: TopUpPayment | null;
}

/** See {@link TopUpView.payment}. Money in `currency`; price lines null while the tariff is unpriced. */
export interface TopUpPayment {
  toCard: string;
  cardPrice: string | null;
  percent: string | null;
  fee: string | null;
  total: string | null;
  currency: string;
  /** Priced on the terms the request is executed on; false — the form's estimate. */
  fixed: boolean;
  /**
   * What to transfer as the payment service named it with the requisites now on screen (e.g. 19 522.25 AED); null
   * before them or when only their text names it. Absent on an older core.
   */
  askAmount?: string | null;
  askCurrency?: string | null;
}

/** See {@link TopUpView.breakdown}. Major units of the deposit asset; `cardPrice` is "0.00" when none. */
export interface TopUpBreakdown {
  received: string;
  cardPrice: string;
  fee: string;
  /** The top-up fee the client was told ("top-up 5%"); null when nothing was charged as a percent. */
  percent?: string | null;
}

/** The client's ephemeral deposit address + coarse phase (§6.1). Single-use per request. */
export interface TopUpDepositView {
  network: string;
  asset: string;
  address: string;
  expiresAt: string;
  /**
   * AWAITING_CARD: the money is here and clean, and the sweep has nowhere to go yet because the client
   * has no card wallet. Distinct from PROCESSING on purpose — PROCESSING implies network work that
   * finishes on its own, this one waits on an operator issuing a card, and the "credited within an
   * hour" promise must not be shown for it.
   *
   * SENT_TO_CARD: our transfer to the card confirmed, the card provider has not reported the credit yet (owner,
   * 28.09). Nothing is asked of the client; "Карта пополнена" comes with DONE, on the provider's own event.
   *
   * BELOW_MINIMUM: money arrived, but what has not moved yet is under `minimum` — nothing happens until more comes
   * to the same address. Not PROCESSING: "обрабатываем" over 1 USDT promised a credit that was never coming.
   */
  phase: 'WAITING_FUNDS' | 'FUNDS_SEEN' | 'PROCESSING' | 'BELOW_MINIMUM' | 'AWAITING_CARD' | 'SENT_TO_CARD' | 'DONE'
    | 'RETURNED' | 'EXPIRED';
  /** Truncated hash of the first seen tx — "we can see your transfer"; null before that. */
  seenTxHash: string | null;
  /** The smallest sum the rail moves, USD — named on the BELOW_MINIMUM line (absent on an older core). */
  minimum?: string | null;
  /** What the client sent under this request, asset major units — named on the BELOW_MINIMUM line. */
  received?: string | number | null;
}

export interface SubmitResult {
  id: string;
  status: string;
  ticketId: string | null;
}

export interface SubmitBody {
  /**
   * What the client sends, in `amountCurrency` — on a bank/cash top-up too since 03.11.2026 («currency first»: the core
   * derives the credit from it). Null when a form names the credit to the card instead (`creditAmount`, В1).
   */
  amount: string | number | null;
  amountCurrency: string;
  /** В1: how much to credit to the card, in the card currency (bank/cash while `fiatCredit` is offered). */
  creditAmount?: string | number | null;
  sourceKind: SourceKind;
  // CRYPTO
  network?: string | null;
  asset?: string | null;
  address?: string | null;
  ownedByClient?: boolean | null;
  // BANK
  bankCountry?: string | null;
  bankName?: string | null;
  senderType?: string | null;
  accountOwnedByClient?: boolean | null;
  accountLast4?: string | null;
  // CASH
  country?: string | null;
  city?: string | null;
  cashCurrency?: string | null;
  availableFrom?: string | null;
  availableTo?: string | null;
  clientComment?: string | null;
}

const BASE = '/v1/showcase/me/topup-requests';

export async function submitTopUp(body: SubmitBody): Promise<SubmitResult> {
  return (await http.post<SubmitResult>(BASE, body)).data;
}

/**
 * Indicative fee breakdown for a proposed amount (PRIVATE_BANK_ONBOARDING_V2.md §5.2), or a feePending state
 * (fixed price + "after approval") when the tariff is not determined.
 *
 * The amount the client typed is what gets CREDITED; the card price and the commission are added on top. Every
 * line is quoted in the currency the client chose — the card price is converted into it server-side.
 */
export interface TopUpEstimate {
  /** What lands on the card — the amount the client asked for, untouched by the fees. */
  grossAmount: string;
  currency: string;
  /** One-off card price, already in `currency`; null on a repeat top-up. */
  issuanceFee: string | null;
  /** Currency of the lines below: equals `currency`, except in the feePending case. */
  feeCurrency: string | null;
  percentFee: string | null;
  /** TOTAL the client pays: credited + commission + card price. Named for the wire contract, not the meaning. */
  netCredit: string | null;
  clientPercent: string | null;
  feePending: boolean;
  /**
   * «Currency first» (docs/TARIFF_RATE_MARKUP.md, owner 03.11.2026): present on a bank/cash estimate asked with its
   * `sourceKind` — the client typed what they want ON THE ACCOUNT in `sendCurrency`, every commission on top; the lines
   * above are then in the card currency (on the card at the market · card price · commission · `netCredit` = `fund`).
   * Absent on the other estimates and on an older core.
   */
  conversion?: TopUpConversion | null;
}

/**
 * See {@link TopUpEstimate.conversion}. The rate is card currency per 1 `sendCurrency` — the FINAL one only (owner,
 * 03.11.2026: «Только итоговый курс»): the market rate and the markup are never sent to the cabinet.
 */
export interface TopUpConversion {
  /** What the client wants on the account, in `sendCurrency`. */
  accountAmount: string;
  sendCurrency: string;
  /** The rate the client pays at; null while the pair's rate is unresolved (`fxUnresolved`). */
  clientRate: string | null;
  /** What Transfer Hub sends to fund the card, in `fundAsset`. */
  fund: string | null;
  fundAsset: string;
  /** What the client pays, everything on top, in `sendCurrency`: fund / clientRate. Null while unresolved. */
  payAmount: string | null;
  fxUnresolved: boolean;
}

/**
 * Пороги, которые клиент должен знать ДО ввода суммы (REQ-040).
 *
 * `cashMinimum === null` — порог не применяется (не настроен или курс не разрешился); подпись не показываем
 * и отправку не блокируем: на сервере проверка в этом случае тоже не срабатывает.
 */
export interface TopUpLimits {
  /** Минимум для наличных в `currency`; null ⇒ порога нет. */
  cashMinimum: string | null;
  currency: string;
  /** Тот же порог в USD — исходная величина настройки. */
  cashMinimumUsd: string | null;
}

export async function topUpLimits(currency: string): Promise<TopUpLimits> {
  return (await http.get<TopUpLimits>(`${BASE}/limits`, { params: { currency } })).data;
}

/**
 * @param sourceKind BANK/CASH — a «currency first» estimate: `amount` is what the client pays in `currency` (see
 *                   {@link TopUpEstimate.conversion}); omitted — the amount is the credit (or a crypto amount).
 */
export async function estimateTopUp(amount: string, currency: string, sourceKind?: SourceKind | null): Promise<TopUpEstimate> {
  const params: Record<string, string> = { amount, currency };
  if (sourceKind) params.sourceKind = sourceKind;
  return (await http.get<TopUpEstimate>(`${BASE}/estimate`, { params })).data;
}

export async function listTopUps(): Promise<TopUpView[]> {
  return (await http.get<TopUpView[]>(BASE)).data;
}

export async function getTopUp(id: string): Promise<TopUpView> {
  return (await http.get<TopUpView>(`${BASE}/${id}`)).data;
}

export async function cancelTopUp(id: string): Promise<void> {
  await http.post(`${BASE}/${id}/cancel`);
}

/** PayFlow spec 07 T29: ask the payment service for a refund (the request itself stays open). */
export async function requestTopUpRefund(id: string): Promise<void> {
  await http.post(`${BASE}/${id}/request-refund`);
}

/** PayFlow spec 07 T33: the client's text answer to "information needed". */
export async function replyToTopUp(id: string, text: string): Promise<void> {
  await http.post(`${BASE}/${id}/reply`, { text });
}

export async function markTopUpSent(id: string): Promise<void> {
  await http.post(`${BASE}/${id}/mark-sent`);
}

/** Client hides a finished request from their list (terminal only; the row + chat thread are retained). */
export async function dismissTopUp(id: string): Promise<void> {
  await http.post(`${BASE}/${id}/dismiss`);
}

const ACTIVE = new Set(['SUBMITTED', 'SCREENING', 'NEEDS_INFO', 'APPROVED', 'EXPORTED', 'IN_PROGRESS', 'FUNDS_RECEIVED']);
export function isActive(status: string): boolean {
  return ACTIVE.has(status);
}
